import { useState } from 'react';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { Plus, Settings2, Trash2, Search, Percent, Boxes } from 'lucide-react';
import { GSTConfigModal, FormatConfigModal, TableColumnsModal } from './TableConfigModals';
import type { OrderState } from '../../pages/CreateSalesOrderPage';
import { useUnits, useProducts, type ProductHierarchyItem } from '@/features/catalog/hooks/useCatalogApi';
import { ProductHierarchySelector } from '@/features/catalog/components/ProductHierarchySelector';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { packSummary, totalBaseQuantityLabel } from '@/shared/utils/ratePerBaseUnit';
import { abbreviatePackaging, derivePackagingType } from '@/shared/utils/packagingAbbreviation';

interface Props {
  state: OrderState;
  updateState: (updater: (prev: OrderState) => OrderState) => void;
}

export function LineItemsTable({ state, updateState }: Props) {
  const [activeDesc, setActiveDesc] = useState<number | null>(null);
  const [activeImg, setActiveImg] = useState<number | null>(null);

  const [showGST, setShowGST] = useState(false);
  const [showFormat, setShowFormat] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [showHierarchySelector, setShowHierarchySelector] = useState(false);

  const handleHierarchySelect = (
    product: ProductHierarchyItem,
    qty: number,
    customRate?: number
  ) => {
    const rate = customRate !== undefined ? customRate : (product.sellingPrice || product.mrp || 0);
    const amount = qty * rate;
    const gstRate = 18;
    const taxAmount = (amount * gstRate) / 100;
    updateState((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Math.random().toString(),
          sku: product.sku,
          description: `${product.name} [Vol: ${(product.volumeLiters * qty).toFixed(1)}L]`,
          image: null,
          hsn: '2710',
          gstRate,
          qty,
          rate,
          amount: Number(amount.toFixed(2)),
          cgst: Number((taxAmount / 2).toFixed(2)),
          sgst: Number((taxAmount / 2).toFixed(2)),
          total: Number((amount + taxAmount).toFixed(2)),
          productId: product._id,
          unitId: '',
          unit: abbreviatePackaging(derivePackagingType(product)),
          packSize: product.packSize || '',
          qtyPerPack: Number(product.qtyPerPack) || 0,
          totalPackSize: product.totalPackSize || '',
          volumeLiters: Number(product.volumeLiters) || 0,
          gradeId: product.gradeId || '',
          grade: product.grade,
          packagingTypeId: '',
          packagingType: product.packagingType,
          batchRequired: false,
        },
      ],
    }));
    setShowHierarchySelector(false);
  };

  // Gap-audit follow-up: Grade/Unit/Packaging per line — real catalogue/
  // master data, not the free text the spec's mandatory-fields audit found
  // missing entirely at Sales Order stage.
  const { data: units = [] } = useUnits();
  const { data: productPage } = useProducts();
  const products = productPage?.data ?? [];
  const { data: grades = [] } = useMasterItems('product_grade');
  const { data: packagingTypes = [] } = useMasterItems('packaging_type');

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(state.numberFormat.locale, {
      minimumFractionDigits: state.numberFormat.currencyDecimals,
      maximumFractionDigits: state.numberFormat.currencyDecimals
    }).format(num);
  };
  
  const handleItemChange = (index: number, field: string, value: string | number | null) => {
    updateState(prev => {
      const newItems = [...prev.items];
      const item = { ...newItems[index], [field]: value };
      
      // Dynamic math engine
      if (['qty', 'rate', 'gstRate'].includes(field)) {
        const qty = field === 'qty' ? Number(value) : item.qty;
        const rate = field === 'rate' ? Number(value) : item.rate;
        const gstRate = field === 'gstRate' ? Number(value) : item.gstRate;
        
        const total = qty * rate;
        const inclusive = state.gstConfig.taxCalculation === 'inclusive';
        const amount = inclusive ? total / (1 + (gstRate / 100)) : total;
        const taxAmount = inclusive ? total - amount : amount * (gstRate / 100);
        
        item.amount = Number(amount.toFixed(2));
        item.cgst = Number((taxAmount / 2).toFixed(2));
        item.sgst = Number((taxAmount / 2).toFixed(2));
        item.total = Number((amount + taxAmount).toFixed(2));
      }
      
      newItems[index] = item;
      return { ...prev, items: newItems };
    });
  };

  /** Picking a catalogue product fills the blanks it can answer, never overwrites what's already typed. */
  const selectProduct = (index: number, productId: string) => {
    updateState(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      const product = products.find((p: any) => p._id === productId);
      if (!product) { newItems[index] = { ...item, productId: '' }; return { ...prev, items: newItems }; }
      const unit = units.find((u: any) => u._id === product.unitId);
      newItems[index] = {
        ...item,
        productId,
        sku: product.sku || item.sku,
        description: item.description || product.name || '',
        unitId: item.unitId || product.unitId || '',
        unit: item.unit || abbreviatePackaging(derivePackagingType(product)) || (unit ? (unit.shortName || unit.name) : ''),
        packSize: item.packSize || product.packSize || '',
        qtyPerPack: Number(item.qtyPerPack || product.qtyPerPack) || 0,
        totalPackSize: item.totalPackSize || product.totalPackSize || '',
        volumeLiters: Number(item.volumeLiters || product.volumeLiters) || 0,
      };
      return { ...prev, items: newItems };
    });
  };

  const removeLine = (index: number) => {
    updateState(prev => {
      const newItems = [...prev.items];
      newItems.splice(index, 1);
      return { ...prev, items: newItems };
    });
  };

  return (
    <div className="flex flex-col">
      {/* Table Toolbar */}
      <div className="flex items-center justify-between gap-3 border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2" onClick={() => setShowGST(true)}>
            <Percent className="w-4 h-4" /> Configure GST
          </Button>
          <div className="flex items-center gap-2">
            <label className="text-label-sm uppercase tracking-wider text-on-surface-variant" htmlFor="so-currency">Currency*</label>
            <select
              id="so-currency"
              className="h-9 rounded-lg border border-input bg-surface px-2.5 text-data-md text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={state.currency}
              onChange={(e) => updateState(s => ({ ...s, currency: e.target.value }))}
            >
              <option value="INR">Indian Rupee (INR, ₹)</option>
              <option value="USD">US Dollar (USD, $)</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="gap-2 text-on-surface-variant" onClick={() => setShowFormat(true)}>
            Number and Currency Format
          </Button>
          <Button variant="ghost" className="gap-2 text-on-surface-variant" onClick={() => setShowColumns(true)}>
            <Settings2 className="w-4 h-4" /> Edit Columns/Formulas
          </Button>
        </div>
      </div>

      {/* Grid Header */}
      <div className="sticky top-0 z-10 grid grid-cols-12 gap-4 border-b border-border bg-muted px-3 py-2 text-label-sm uppercase tracking-wider text-on-surface-variant">
        <div className="col-span-3">Item</div>
        {state.tableConfig.showHsn && <div className="col-span-1">HSN/SAC</div>}
        {state.tableConfig.showGstRate && <div className="col-span-1">GST Rate</div>}
        <div className="col-span-1 text-right">Quantity</div>
        <div className="col-span-1 text-right">Rate</div>
        {state.tableConfig.showAmount && <div className="col-span-1 text-right">Amount</div>}
        {state.tableConfig.showCgst && <div className="col-span-1 text-right">CGST</div>}
        {state.tableConfig.showSgst && <div className="col-span-1 text-right">SGST</div>}
        <div className="col-span-1 text-right">Total</div>
        <div className="col-span-1" />
      </div>

    {/* Grid Rows */}
      <div className="divide-y divide-border">
        {state.items.map((item, index) => (
          <div key={item.id} className="p-3 transition-colors hover:bg-muted/50">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono tabular-nums text-data-sm font-semibold text-on-surface-variant">{index + 1}</span>
              <button onClick={() => removeLine(index)} aria-label={`Remove line ${index + 1}`} className="rounded p-1 text-on-surface-subtle transition-colors hover:bg-destructive/10 hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3 relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-on-surface-subtle" />
                <Input
                  aria-label="Item name or SKU"
                  placeholder="Item Name / SKU Id"
                  className="pl-9"
                  value={item.sku}
                  onChange={(e) => handleItemChange(index, 'sku', e.target.value)}
                />
              </div>
              
              {state.tableConfig.showHsn && (
                <div className="col-span-1">
                  <Input
                    aria-label="HSN/SAC"
                    placeholder="#"
                    value={item.hsn}
                    onChange={(e) => handleItemChange(index, 'hsn', e.target.value)}
                  />
                </div>
              )}
              
              {state.tableConfig.showGstRate && (
                <div className="col-span-1 relative">
                  <Input
                    type="number"
                    aria-label="GST rate"
                    value={item.gstRate}
                    onChange={(e) => handleItemChange(index, 'gstRate', e.target.value)}
                    className="pr-6 text-right font-mono tabular-nums"
                  />
                  <span className="pointer-events-none absolute right-2 top-2.5 text-body-sm text-on-surface-subtle">%</span>
                </div>
              )}
              
              <div className="col-span-1">
                <Input
                  type="number"
                  aria-label="Quantity"
                  value={item.qty}
                  onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                  className="text-right font-mono tabular-nums"
                />
                {totalBaseQuantityLabel(item.qty, item) && (
                  <p className="mt-1 text-right font-mono tabular-nums text-data-xs font-semibold text-success-text">
                    {totalBaseQuantityLabel(item.qty, item)}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <Input
                  type="number"
                  aria-label="Rate"
                  value={item.rate}
                  onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                  className="text-right font-mono tabular-nums"
                />
              </div>
              
              {state.tableConfig.showAmount && (
                <div className="col-span-1 text-right font-mono tabular-nums text-data-md text-on-surface">{formatNumber(item.amount)}</div>
              )}
              {state.tableConfig.showCgst && (
                <div className="col-span-1 text-right font-mono tabular-nums text-data-md text-on-surface-variant">{formatNumber(item.cgst)}</div>
              )}
              {state.tableConfig.showSgst && (
                <div className="col-span-1 text-right font-mono tabular-nums text-data-md text-on-surface-variant">{formatNumber(item.sgst)}</div>
              )}
              <div className="col-span-1 text-right font-mono tabular-nums text-data-md font-semibold text-on-surface">{formatNumber(item.total)}</div>
              <div className="col-span-1" />
            </div>

            {/* Gap-audit follow-up: Grade/Unit/Packaging/Batch — mandatory
                per the client spec, previously not captured at Sales Order
                stage at all. Catalogue product picker fills description/
                unit/SKU when the item is a real catalogue product; the free
                "Item Name / SKU Id" box above still works for anything else. */}
            <div className="mt-3 grid grid-cols-5 gap-3">
              <select
                aria-label="Catalogue item"
                className="col-span-1 h-9 rounded-lg border border-input bg-surface px-2 text-body-xs text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={item.productId}
                onChange={(e) => selectProduct(index, e.target.value)}
              >
                <option value="">Catalogue item…</option>
                {products.map((p: any) => <option key={p._id} value={p._id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
              </select>
              <select
                aria-label="Grade"
                className="col-span-1 h-9 rounded-lg border border-input bg-surface px-2 text-body-xs text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={item.gradeId}
                onChange={(e) => {
                  const g = grades.find((x: any) => x._id === e.target.value);
                  updateState(prev => { const items = [...prev.items]; items[index] = { ...items[index], gradeId: e.target.value, grade: g?.name || '' }; return { ...prev, items }; });
                }}
              >
                <option value="">Grade…</option>
                {grades.map((g: any) => <option key={g._id} value={g._id}>{g.name}</option>)}
              </select>
              <select
                aria-label="Unit"
                className="col-span-1 h-9 rounded-lg border border-input bg-surface px-2 text-body-xs text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={item.unitId}
                onChange={(e) => {
                  const u = units.find((x: any) => x._id === e.target.value);
                  updateState(prev => { const items = [...prev.items]; items[index] = { ...items[index], unitId: e.target.value, unit: u ? (u.shortName || u.name) : '' }; return { ...prev, items }; });
                }}
              >
                <option value="">Unit…</option>
                {units.map((u: any) => <option key={u._id} value={u._id}>{u.name}{u.shortName ? ` (${u.shortName})` : ''}</option>)}
              </select>
              <select
                aria-label="Packaging type"
                className="col-span-1 h-9 rounded-lg border border-input bg-surface px-2 text-body-xs text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={item.packagingTypeId}
                onChange={(e) => {
                  const pk = packagingTypes.find((x: any) => x._id === e.target.value);
                  updateState(prev => { const items = [...prev.items]; items[index] = { ...items[index], packagingTypeId: e.target.value, packagingType: pk?.name || '' }; return { ...prev, items }; });
                }}
              >
                <option value="">Packaging…</option>
                {packagingTypes.map((pk: any) => <option key={pk._id} value={pk._id}>{pk.name}</option>)}
              </select>
              <label className="col-span-1 flex items-center gap-1.5 text-body-sm text-on-surface-variant">
                <input
                  type="checkbox"
                  className="rounded border-input text-primary focus-visible:ring-ring"
                  checked={item.batchRequired}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateState(prev => { const items = [...prev.items]; items[index] = { ...items[index], batchRequired: checked }; return { ...prev, items }; });
                  }}
                />
                Batch required
              </label>
            </div>

            {(activeDesc === index || item.description) && (
              <div className="mt-3">
                <Input
                  aria-label="Item description"
                  placeholder="Enter item description..."
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  className="w-full text-body-sm text-on-surface-variant"
                  autoFocus={activeDesc === index}
                />
                {packSummary(item) && (
                  <p className="mt-1 text-right font-mono tabular-nums text-data-xs font-semibold text-success-text">
                    {packSummary(item)}
                  </p>
                )}
              </div>
            )}
            
            {(activeImg === index || item.image) && (
              <div className="mt-3 flex items-center gap-3 rounded-card-sm border border-dashed border-outline-variant bg-muted/50 p-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-data-xs text-on-surface-subtle">
                   {item.image ? <img src={item.image} alt="Item" className="w-full h-full object-cover" /> : 'IMG'}
                </div>
                <div className="flex-1">
                  <Input
                    aria-label="Image URL"
                    placeholder="Enter image URL..."
                    value={item.image || ''}
                    onChange={(e) => handleItemChange(index, 'image', e.target.value)}
                    className="w-full text-body-sm"
                    autoFocus={activeImg === index}
                  />
                </div>
                <button
                  onClick={() => { handleItemChange(index, 'image', null); setActiveImg(null); }}
                  className="text-body-xs font-medium text-destructive hover:underline"
                >
                  Remove
                </button>
              </div>
            )}

            <div className="mt-3 flex gap-4 text-body-sm font-medium text-primary">
              {(!item.description && activeDesc !== index) && (
                <button onClick={() => setActiveDesc(index)} className="flex items-center gap-1 hover:underline">
                  <span className="text-lg leading-none">+</span> Add Description
                </button>
              )}
              {(!item.image && activeImg !== index) && (
                <button onClick={() => setActiveImg(index)} className="flex items-center gap-1 hover:underline">
                  <span className="text-lg leading-none">+</span> Add Image
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* RBM Product Selector — Division/Group/Grade/Pack Size */}
      {showHierarchySelector && (
        <div className="border-t border-border bg-muted/40 p-4">
          <ProductHierarchySelector
            onSelectProduct={handleHierarchySelect}
            onCancel={() => setShowHierarchySelector(false)}
          />
        </div>
      )}

      {/* Grid Footer Actions */}
      <div className="flex items-center justify-center gap-4 border-t border-border bg-muted/40 p-4">
        <Button
          type="button"
          onClick={() => setShowHierarchySelector(!showHierarchySelector)}
          className="w-1/2 gap-2 font-semibold"
        >
          <Boxes className="w-4 h-4" /> {showHierarchySelector ? 'Hide Product Selector' : 'RBM Product Selector'}
        </Button>
        <Button variant="outline" className="w-1/2 gap-2 border-dashed border-outline-variant text-on-surface-variant">
          <Plus className="w-4 h-4" /> Add New Group
        </Button>
      </div>
      
      {showGST && <GSTConfigModal state={state} updateState={updateState} onClose={() => setShowGST(false)} />}
      {showFormat && <FormatConfigModal state={state} updateState={updateState} onClose={() => setShowFormat(false)} />}
      {showColumns && <TableColumnsModal state={state} updateState={updateState} onClose={() => setShowColumns(false)} />}
    </div>
  );
}
